import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Divider,
  Alert
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as ReportsIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CompleteIcon
} from '@mui/icons-material';
// Mock data removed - will fetch from API

const RepSales = ({ currentUser }) => {
  const [timeFilter, setTimeFilter] = useState('current_month');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Temporary placeholders - will be fetched from API
  const mockOrders = [];
  const mockClinics = [];
  const mockCompanies = [];
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Filter orders for this rep's clinics
  const repOrders = useMemo(() => {
    if (!currentUser?.clinicIds) return [];
    
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return mockOrders.filter(order => {
      const orderClinic = mockClinics.find(clinic => 
        clinic.companyId === order.customerId || clinic.id === order.customerId
      );
      if (!orderClinic || !currentUser.clinicIds.includes(orderClinic.id)) {
        return false;
      }
      
      const orderDate = new Date(order.orderDate);
      const orderMonth = orderDate.getMonth();
      const orderYear = orderDate.getFullYear();
      
      let timeMatch = true;
      switch (timeFilter) {
        case 'current_month':
          timeMatch = orderMonth === currentMonth && orderYear === currentYear;
          break;
        case 'previous_month':
          const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
          const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
          timeMatch = orderMonth === prevMonth && orderYear === prevYear;
          break;
        case 'last_3_months':
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          timeMatch = orderDate >= threeMonthsAgo;
          break;
        case 'ytd':
          timeMatch = orderYear === currentYear;
          break;
        default:
          timeMatch = true;
      }
      
      const statusMatch = statusFilter === 'all' || order.status === statusFilter;
      
      const searchMatch = searchTerm === '' || 
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        orderClinic.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      return timeMatch && statusMatch && searchMatch;
    });
  }, [currentUser, timeFilter, statusFilter, searchTerm]);

  // Calculate summary metrics
  const totalSales = repOrders.reduce((sum, order) => sum + order.totalAmount, 0);
  const totalOrders = repOrders.length;
  const avgOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;
  
  // Status breakdown
  const statusBreakdown = repOrders.reduce((acc, order) => {
    acc[order.status] = (acc[order.status] || 0) + 1;
    return acc;
  }, {});

  const SummaryCard = ({ title, value, subtitle, icon, color = '#0d9488' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color, mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const OrderDetailDialog = ({ order, open, onClose }) => {
    if (!order) return null;
    
    const clinic = mockClinics.find(c => 
      c.companyId === order.customerId || c.id === order.customerId
    );
    const company = mockCompanies.find(comp => comp.id === clinic?.companyId);
    
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Order Details - {order.id}
            </Typography>
            <Chip 
              label={order.status}
              size="small"
              sx={{
                bgcolor: order.status === 'Completed' ? '#dcfce7' : 
                       order.status === 'Processing' ? '#fef3c7' : '#fee2e2',
                color: order.status === 'Completed' ? '#16a34a' : 
                      order.status === 'Processing' ? '#d97706' : '#dc2626',
                fontWeight: 600
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Customer Information
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {clinic?.name || 'Unknown Clinic'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {company?.name || 'Unknown Company'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Order Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Product:</strong> {order.productName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Quantity:</strong> {order.quantity} vials
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Total Amount:</strong> ${order.totalAmount.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <strong>Order Date:</strong> {new Date(order.orderDate).toLocaleDateString()}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" sx={{ bgcolor: '#0d9488' }}>
            Export Details
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
          My Sales Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and track orders from your assigned clinics
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Revenue"
            value={`$${totalSales.toLocaleString()}`}
            subtitle={`${totalOrders} orders`}
            icon={<TrendingUpIcon />}
            color="#0d9488"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Avg Order Value"
            value={`$${avgOrderValue.toLocaleString()}`}
            subtitle="Per transaction"
            icon={<ReportsIcon />}
            color="#0f766e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Completed Orders"
            value={statusBreakdown.Completed || 0}
            subtitle="Successfully delivered"
            icon={<CompleteIcon />}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="In Transit"
            value={statusBreakdown.Processing || 0}
            subtitle="Currently shipping"
            icon={<ShippingIcon />}
            color="#f59e0b"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={timeFilter}
                  label="Time Period"
                  onChange={(e) => setTimeFilter(e.target.value)}
                >
                  <MenuItem value="current_month">Current Month</MenuItem>
                  <MenuItem value="previous_month">Previous Month</MenuItem>
                  <MenuItem value="last_3_months">Last 3 Months</MenuItem>
                  <MenuItem value="ytd">Year to Date</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Processing">Processing</MenuItem>
                  <MenuItem value="Shipped">Shipped</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search orders, products, or clinics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                fullWidth
                sx={{ borderColor: '#0d9488', color: '#0d9488' }}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Orders ({repOrders.length})
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<FilterIcon />}
                sx={{ borderColor: '#0d9488', color: '#0d9488' }}
              >
                More Filters
              </Button>
            </Box>
          </Box>
          
          {repOrders.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No orders found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or time period
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Clinic</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {repOrders.map((order) => {
                    const clinic = mockClinics.find(c => 
                      c.companyId === order.customerId || c.id === order.customerId
                    );
                    const company = mockCompanies.find(comp => comp.id === clinic?.companyId);
                    
                    return (
                      <TableRow key={order.id} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                        <TableCell sx={{ color: '#0f172a', fontWeight: 600 }}>
                          {order.id}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                              {clinic?.name || 'Unknown Clinic'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {company?.name || 'Unknown Company'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#374151' }}>
                          {order.productName}
                        </TableCell>
                        <TableCell sx={{ color: '#0f172a', fontWeight: 600 }}>
                          {order.quantity}
                        </TableCell>
                        <TableCell sx={{ color: '#0f172a', fontWeight: 600 }}>
                          ${order.totalAmount.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.status}
                            size="small"
                            sx={{
                              bgcolor: order.status === 'Completed' ? '#dcfce7' : 
                                     order.status === 'Processing' ? '#fef3c7' : '#fee2e2',
                              color: order.status === 'Completed' ? '#16a34a' : 
                                    order.status === 'Processing' ? '#d97706' : '#dc2626',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#374151' }}>
                          {new Date(order.orderDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small"
                            onClick={() => setSelectedOrder(order)}
                            sx={{ color: '#0d9488' }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <OrderDetailDialog
        order={selectedOrder}
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />
    </Box>
  );
};

export default RepSales;